/**
 * useInstrumentSearch — shared hook for instrument search.
 *
 * Debounced search against the backend's unified search endpoint.
 * Returns matching instruments (stocks, ETFs, indices, MF, etc.).
 *
 * Usage:
 *   const { results, loading, search, clear } = useInstrumentSearch();
 *   search('RELIANCE');
 */
import { useState, useCallback, useRef } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

const DEBOUNCE_MS = 300;

export function useInstrumentSearch({ limit = 20 } = {}) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);
  const abortRef = useRef(null);

  const search = useCallback((query) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (abortRef.current) abortRef.current.abort();

    if (!query || query.trim().length < 1) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    timerRef.current = setTimeout(async () => {
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const res = await axios.get(
          `${API}/search?q=${encodeURIComponent(query.trim())}&limit=${limit}`,
          { signal: controller.signal },
        );
        setResults(res.data?.results || res.data || []);
        setError(null);
      } catch (err) {
        if (err.name !== 'CanceledError' && err.code !== 'ERR_CANCELED') {
          setError(err.message);
        }
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);
  }, [limit]);

  const clear = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (abortRef.current) abortRef.current.abort();
    setResults([]);
    setLoading(false);
    setError(null);
  }, []);

  return { results, loading, error, search, clear };
}
